import { type Editor, type Range } from '@tiptap/core';

import {
  WORKSPACE_AGENT_PILL_NODE_NAME,
  type WorkspaceAgentPillNodeAttrs,
} from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/workspaceAgentPill';

export type InsertWorkspaceAgentPillOptions = {
  attrs: WorkspaceAgentPillNodeAttrs;
  editor: Editor;
  /** The `@tiptap/suggestion` range to replace. */
  range?: Range;
};

/**
 * Inserts a pill atom plus a trailing space. With a `range` (a `@tiptap/suggestion` callback, always
 * focused) `chain().focus()` is safe; without one the editor may be unfocused, and only
 * `editor.view.focus()` avoids TipTap's mismatched-transaction error.
 */
export const insertWorkspaceAgentPill = ({ attrs, editor, range }: InsertWorkspaceAgentPillOptions): boolean => {
  if (editor.isDestroyed) {
    return false;
  }

  const content = [
    { attrs, type: WORKSPACE_AGENT_PILL_NODE_NAME },
    { text: ' ', type: 'text' },
  ];

  if (range) {
    // From `@tiptap/extension-mention`: when the caret already sits before a space, the trailing space would double up.
    const nodeAfter = editor.view.state.selection.$to.nodeAfter;
    const overrideSpace = nodeAfter?.text?.startsWith(' ') ?? false;
    const insertRange = overrideSpace ? { from: range.from, to: range.to + 1 } : range;

    const didInsert = editor.chain().focus().insertContentAt(insertRange, content).scrollIntoView().run();

    // Chrome and Safari can leave the caret inside the atom's node view; collapse the DOM selection after the space.
    const selection = window.getSelection();

    if (selection && selection.rangeCount > 0) {
      selection.collapseToEnd();
    }

    return didInsert;
  }

  editor.view.focus();

  return editor.chain().insertContent(content).scrollIntoView().run();
};
